/**
 * @author Trendrating <info@trendrating.net>
 *
 * @module api/compute/SystematicProducts
 * @summary Requests for Systematic products
 *
 */

import { httpAll } from "../../httpAll";
// import { TDate } from "../../trendrating/date/TDate";
import { _Base } from "../_Base";
import { endpoints } from "../endpoints";
import { Lists } from "./Lists";
import { Strategies } from "./Strategies";
import { Subscriptions } from "./Subscriptions";
import { Utils } from "./Utils";

export class SystematicProducts extends _Base {
  apiSubscriptions: Subscriptions;
  apiUtils: Utils;
  apiList: any;
  apiStrategies: any;
  constructor(userData?) {
    super();
    if (userData) {
      this.apiList = new Lists(userData);
      this.apiStrategies = new Strategies(userData);
    } else {
      this.apiList = new Lists();
      this.apiStrategies = null;
    }
    this.apiUtils = new Utils();
    this.apiSubscriptions = new Subscriptions();
  }

  public async select(payload: {
    searches: { filters: { dimension: string; segments: any[] }[] }[];
    sort: { dimension: string; rev: boolean };
  }) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.systematicPortfolios.select;

    const response = await this.preparePost(url, payload);

    return response?.data?.ids;
  }

  public async fetch({
    ids,
    properties,
  }: {
    ids: number[];
    properties: string[];
  }) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.common.fetch;

    const params = [
      {
        classType: "SYSTEMATIC_PRODUCT",
        id: ids,
        extendedRequest: {
          onDemandResult: properties.map((property) => ({
            dimension: property,
          })),
        },
      },
    ];

    try {
      const response = await this.preparePost(url, params);

      let remappedRows: any = [];

      if (response) {
        for (const row of response?.data?.[0].rows) {
          const remappedObj = {};
          let exploded: any = null;

          for (const key in row) {
            if (key.startsWith("object.")) {
              exploded = key.split(".");

              remappedObj[exploded[exploded.length - 1]] = row[key];
            } else {
              remappedObj[key] = row[key];
            }
          }

          remappedRows.push(remappedObj);
        }

        response.data[0].rows = remappedRows;
      }

      return response?.data?.[0].rows ?? undefined;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }

  async getAutoRebalancing(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    let url =
      endPointRoot + endpoints.systematicPortfolios.getByIdAutorebalanced;

    const paramsFilter = {
      searches: [
        {
          filters: [
            {
              dimension: "autorebalance",
              segments: [true],
            },
          ],
        },
      ],
    };

    if (params != null && params.userIds != null) {
      paramsFilter.searches[0].filters.push({
        dimension: "ownerId",
        segments: params.userIds,
      });
    }

    const response = await this.preparePost(url, paramsFilter, null);

    url = endPointRoot + endpoints.common.fetch;

    const items = response["data"]["rows"];
    const itemsIds = items.map((item: any) => item.id);

    const paramsFetch = [
      {
        classType: "SYSTEMATIC_PRODUCT",
        id: itemsIds,
        extendedRequest: {
          onDemandResult: [
            {
              dimension: "name",
            },
            {
              dimension: "ownerId",
            },
            {
              dimension: "historicalPortfolioId",
            },
            {
              dimension: "rebalanceTime",
            },
            {
              dimension: "reviewGranularity",
            },
            {
              dimension: "strategyId",
            },

            { dimension: "calendarRebalance" },
          ],
        },
      },
    ];

    const responseFetch = await this.preparePost(url, paramsFetch, null);
    return responseFetch.data[0]["rows"];
  }

  async rebalanceInfo(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.systematicPortfolios.rebalanceInfoById;

    const _params = {
      reviewGranularity: params["reviewGranularity"],
      systematicProductId: params["id"],
    };

    const response = await this.prepareGet(url, _params, null);
    return this._normalizeRebalanceInfo(response);
  }

  _normalizeRebalanceInfo(response: any) {
    const _response = response["data"];

    return _response;
  }

  async get(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    if (params != null && params["id"] != null) {
      const url = endPointRoot + endpoints.systematicPortfolios.getById;

      try {
        const response = await httpAll({
          systematicPortfolio: this.prepareGet(url, params, null),
          subscriptions: this.apiSubscriptions.get({
            type: "systematicPortfolio",
            user_id: params["ownerId"],
          }),
        });

        const systematicPortfolio: any = this._normalizeGet(
          response["systematicPortfolio"],
        );

        // Inject isReadOnly flag
        if (response["subscriptions"] != null) {
          if (
            this.apiSubscriptions.isSubscription(
              systematicPortfolio,
              response["subscriptions"],
            ) === true
          ) {
            systematicPortfolio["isReadOnly"] = true;
          }
        }

        return systematicPortfolio;
      } catch (error: any) {
        // Server returns 500 instead 404 when a list is not
        // available (not exist or has been deleted)
        error["initiator"] =
          "trendrating/api/compute/SystematicPortfolios.getWithSubscriptions(params)";

        return this.simulateHttpError(error);
      }
    }

    params = {
      ownerId: params["ownerId"],
    };
    const url = endPointRoot + endpoints.systematicPortfolios.get;

    const response = await httpAll({
      subscriptions: this.apiSubscriptions
        .get({
          type: "systematicPortfolio",
          user_id: params.ownerId,
        })
        .then((response: any) => {
          const endPointRoot = process.env.REACT_APP_ROOT_T_REST;

          const url = endPointRoot + endpoints.systematicPortfolios.getById;

          const requests: any = [];
          for (let i = 0, length = response.length; i < length; i++) {
            requests.push(this.prepareGet(url, { id: response[i].id }, null));
          }

          return httpAll(requests);
        }),
      systematicPortfolios: this.prepareGet(url, params, null),
    });
    if (
      response["systematicPortfolios"] != null &&
      response["systematicPortfolios"]["status"] === "OK"
    ) {
      if (response.subscriptions != null) {
        for (const subscription of response.subscriptions) {
          // Convert shared object response in a
          // server-like pre-normalized format
          const subscribedSystematicPortfolio =
            subscription["data"]["systematicProduct"];
          subscribedSystematicPortfolio.isReadOnly = true;

          response["systematicPortfolios"]["data"]["systematicProducts"].push(
            subscribedSystematicPortfolio,
          );
        }
      }

      return this._normalizeGet(response["systematicPortfolios"]);
    }

    return [];
  }

  _normalizeGet(response: any) {
    function addIsReadOnly(item: any) {
      item["isReadOnly"] = "isReadOnly" in item ? item["isReadOnly"] : false;
      return item;
    }

    function addType(item: any) {
      item["type"] = "SYSTEMATIC_PRODUCT";
      return item;
    }

    if ("systematicProduct" in response["data"]) {
      return addIsReadOnly(addType(response["data"]["systematicProduct"]));
    }

    const _items = response["data"]["systematicProducts"];
    const items: any = [];
    for (let i = 0; i < _items.length; i++) {
      items.push(addIsReadOnly(addType(_items[i])));
    }

    return {
      data: items.sort(function (productA, productB) {
        const nameA = productA["name"].toLowerCase();
        const nameB = productB["name"].toLowerCase();

        if (nameA > nameB) {
          return 1;
        }

        if (nameA < nameB) {
          return -1;
        }

        return 0;
      }),
      dataTotalCount: items.length,
    };
  }

  async newRebalance(params) {
    const smsID = params["id"];
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.systematicPortfolios.executeRebalance;
    try {
      const response = await this.preparePost(url, { id: smsID }, null);
      return response.data;
    } catch (error: any) {
      console.error(error.message);
    }
  }

  async notifyAutoRebalance(data: number[], additionalCookies: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_API;
    const url =
      endPointRoot + endpoints.systematicPortfolios.notifyAutoRebalance;
    try {
      const response = await this.preparePost(
        url,
        { data, additionalCookies },
        null,
      );

      return response;
    } catch (error: any) {
      throw new Error(error.message);
      // console.error(error.message);
    }
  }

  //!old rebalance
  // async rebalance(params: any) {
  //   const strategyId = params["strategyId"];

  //   const _params = {
  //     snapshot: this.snapshot(params),
  //     strategy: this.apiStrategies.get(strategyId),
  //   };

  //   const response = await httpAll(_params);
  //   return this.rebalanceSnaphotStrategy(response);
  // }

  // async snapshot(params: any) {
  //   const _params = {
  //     id: params["historicalPortfolioId"],
  //   };

  //   const response = await this.listHistory(_params);
  //   return this.snapshotActualizer(response);
  // }
  async listHistory(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.systematicPortfolios.history;
    const response = await this.prepareGet(url, params, null);
    return this._normalizeListHistory(response);
  }

  _normalizeListHistory(response: any) {
    const _response = response["data"]["historicalPortfolio"];

    _response["POS"].sort(function (a: any, b: any) {
      if (a["d"] > b["d"]) {
        return 1;
      }
      if (a["d"] < b["d"]) {
        return -1;
      }

      return 0;
    });

    return _response;
  }

  // async snapshotActualizer(response: any) {
  //   const serverStatus = await this.apiUtils.today();
  //   const yesterday = serverStatus["today"];

  //   const lastAllocationIndex = response["POS"].length - 1;
  //   const lastAllocation = response["POS"][lastAllocationIndex];

  //   const params = {
  //     adjustWeights: true,
  //     asOf: TDate.daysToIso8601(yesterday),
  //     currency: response["currency"],
  //     equalWeighted: false,
  //     normalize: false,
  //     perfMetricMode: "active",
  //     v: lastAllocation != null ? lastAllocation["v"] : [],
  //     weightsAsOf: TDate.daysToIso8601(
  //       lastAllocation != null ? lastAllocation["d"] : yesterday
  //     ),
  //   };
  //   return this.ratingAt(params);
  // }

  ratingAt(params: any) {
    //
    // need refactoring: the API must works with API object,
    // Encoding params for server API must be done here not in the
    // storage or cache
    //
    const idAndName =
      "auto-generated-systematic-portfolio-rating-at-" + new Date().getTime();
    const list = Lists.getPrototype();
    list.name = idAndName;
    list.type = "PORTFOLIO";
    list.weightsManagement = {
      currency: params.currency,
      date: params.weightsAsOf,
      type: "drifting",
    };
    list.positions = params.v.map((vPos: any) => ({
      symbol: vPos.S,
      weight: vPos.A,
    }));

    return this.apiList.getPointInTime({
      asOf: params.asOf,
      list: list,
    });
  }

  async rebalanceSnaphotStrategy(response: any) {
    const serverStatus = await this.apiUtils.today();
    const snapshot = response["snapshot"];
    const strategy = response["strategy"];

    const encodedStrategyParams = this.apiStrategies.prepareParamsForRun(
      strategy["params"],
      null,
    );
    const yesterday = serverStatus["today"];

    const exAnteList = {
      d: yesterday,
      v: snapshot.positions.map((position: any) => ({
        A: position["weight"],
        S: position["symbol"],
      })),
    };

    const params = {
      date: yesterday,
      exAntePortfolio: exAnteList,
      strategy: encodedStrategyParams["strategy"],
      universe: encodedStrategyParams["universe"],
      verbose: false,
    };

    const responseRebalance = await this.listRebalance(params);
    return this._rebalance(responseRebalance);
  }
  _rebalance(response: any) {
    const _response = {
      date: response["d"],
      positions: response["v"].map((position: any) => ({
        symbol: position["S"],
        weight: position["A"],
      })),
    };
    return _response;
  }
  async listRebalance(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.lists.rationales;

    const response = await this.preparePost(url, params, null);
    return this._normalizeListRebalance(response);
  }

  _normalizeListRebalance(response: any) {
    if (response["status"] === "KO") {
      const error = this.simulateHttpError(response);

      if (error != null) {
        return error;
      }
    }

    const _response = response["data"]["allocation"];

    return _response;
  }

  async rebalanceAdd(params: any) {
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.systematicPortfolios.addRebalance;

    const _params = {
      historicalPortfolioId: params["product"]["historicalPortfolioId"],
      d: params["rebalance"]["date"],
      v: params["rebalance"]["positions"].map((position: any) => ({
        A: position["weight"],
        S: position["symbol"],
      })),
    };

    const response = await this.preparePost(url, _params, null);
    return this._normalizeRebalanceAdd(response);
  }

  _normalizeRebalanceAdd(response: any) {
    return this._normalizeListHistory(response);
  }
}
